"use strict";
const _ = require('lodash');
const moment = require('moment');
const { originIsoCodes } = require('./origin-iso-codes');
const { findFirstPathWithProperty: originFindFirstPathWithProperty, } = require('./origin-tree');
const { getImmutablefields, calculateAlcoholTaxClass, calculateBookKeepingAccounts, calculateName, calculateRequiresAlcoholPermit, calculateVAT, calculateProductExciseCode, } = require('./properties');
const { alcoholTaxCalculator } = require('./alcohol-tax-calculator');
const { findProductCode } = require('./kn-numbers-with-product-codes');
const { getTranslation } = require('../translate');
const articleTranslations = require('./article.const.translate');
const { PRIVAB_OWNING_SUPPLIER_ORGANIZATION_ID } = require('../organization');
const setKeywords = entity => {
    const keywords = [];
    if (entity.brand && entity.brand.name && entity.brand.name.length > 0) {
        keywords.push(entity.brand.name);
    }
    if (entity.grapes && entity.grapes.length > 0) {
        keywords.push(entity.grapes);
    }
    return keywords.join(' ').trim();
};
function clearNullLinks(entity) {
    if (!entity.articleLinks || entity.articleLinks.length === 0) {
        return entity.articleLinks;
    }
    return entity.articleLinks.filter(
    // eslint-disable-next-line no-underscore-dangle
    x => x && (x._recentCreate || (x.href && x.href.length > 0)));
}
const ensureArticleBatches = articleBatches => {
    const result = [];
    for (let i = 0; i < articleBatches.length; i++) {
        const x = {
            ...articleBatches[i],
            blockedQuantity: articleBatches[i].blockedQuantity || 0,
            quantityInStock: articleBatches[i].quantityInStock || 0,
        };
        if (x.blockedQuantity > 0) {
            x.isBlocked = true;
        }
        if (x.isBlocked) {
            x.blockedQuantity += x.quantityInStock;
            x.quantityInStock = 0;
        }
        else {
            x.quantityInStock += x.blockedQuantity;
            x.blockedQuantity = 0;
        }
        result.push(x);
    }
    return result;
};
const ensurePrivabArticleNumber = entity => {
    if (entity.owningSupplierOrganizationId ===
        PRIVAB_OWNING_SUPPLIER_ORGANIZATION_ID) {
        if (!entity.articleNumber) {
            throw new Error(`${getTranslation('article', 'articleNumber')} saknas`, 412);
        }
        if (entity.articleNumber.includes('-')) {
            return entity.articleNumber;
        }
        const supplierNumber = _.get(entity, 'supplier.supplierNumber');
        if (!supplierNumber) {
            throw new Error(`${getTranslation('article', 'supplier')} saknas`);
        }
        if (!entity.varuTypDimensionCode) {
            throw new Error(`${getTranslation('article', 'varuTypDimensionCode')} saknas`, 412);
        }
        let prefix = '';
        if (!_.startsWith(entity.varuTypDimensionCode, '0')) {
            const paddedSupplierNumber = _.padStart(supplierNumber, 3, '0');
            prefix = `${entity.varuTypDimensionCode.slice(0, 1)}${paddedSupplierNumber}`;
        }
        else {
            prefix = supplierNumber;
        }
        return `${prefix}-${entity.articleNumber}`;
    }
    return entity.articleNumber;
};
const ensurePrivabColliSize = entity => {
    if (entity.owningSupplierOrganizationId ===
        PRIVAB_OWNING_SUPPLIER_ORGANIZATION_ID) {
        return 1;
    }
    return entity.colliSize;
};
const ensureSalesPeriod = entity => {
    const result = {
        ...entity,
    };
    if (result.salesStart && moment().isBefore(moment(result.salesStart))) {
        result.isPublishedOnPro = false;
        result.isPublishedOnOurStore = false;
        result.isPublishedOnWhitelabel = false;
    }
    if (result.salesEnd && moment().isAfter(moment(result.salesEnd))) {
        result.isPublishedOnPro = false;
        result.isPublishedOnOurStore = false;
        result.isPublishedOnWhitelabel = false;
    }
    return result;
};
const ensureDropShippingArticle = entity => {
    const result = {
        ...entity,
    };
    return result;
};
const calculateQuantityInStock = articleBatches => {
    if (articleBatches && articleBatches.length > 0) {
        const nonBlockedBatches = articleBatches.filter(x => !x.isBlocked);
        return _.sumBy(nonBlockedBatches, x => x.quantityInStock);
    }
    return 0;
};
const ensureIsCopack = entity => {
    // when one of the articleBatches is on vinden or exacta, the article is a copack article
    const result = {
        ...entity,
    };
    if (result.articleBatches && result.articleBatches.length > 0) {
        result.onCopack = result.articleBatches.some(x => x.orderFullfillmentCenter && x.orderFullfillmentCenter.isCopack);
    }
    return result;
};
const ensurePipeStringOrigin = entity => {
    if (!entity.origin)
        return entity;
    const result = {
        ...entity,
    };
    const country = originIsoCodes[entity.origin] || entity.origin;
    try {
        result.origin = originFindFirstPathWithProperty('name', country);
        return result;
    }
    catch (e) {
        // do nothing
    }
    try {
        result.origin = originFindFirstPathWithProperty('name', _.startCase(_.lowerCase(country)));
        return result;
    }
    catch (e) {
        // do nothing
    }
    if (country.split(',').length > 1) {
        try {
            const firstPartCountryTry = originIsoCodes[country.split(',')[0]];
            result.origin = originFindFirstPathWithProperty('name', firstPartCountryTry);
            return result;
        }
        catch (e) {
            // do nothing
        }
    }
    result.origin = country;
    return result;
};
const weightUnits = ['kg', 'g', 'hg', 'mg'];
const volumeUnits = ['l', 'ml', 'cl', 'dl', 'liter'];
const getIncrement = altUnit => {
    if (_.includes(weightUnits, altUnit) || _.includes(volumeUnits, altUnit)) {
        return null;
    }
    return 1;
};
const colliUnit = 'kolli';
const ensureSKU = entity => {
    if (entity.metadata && entity.metadata.hasManualSKUs) {
        return entity;
    }
    // convert the old unit and colli definitions to the new SKU format
    const stockKeepingUnits = [];
    const qvantiNormUnit = entity.metadata != null ? entity.metadata.qvantiNormUnit : null;
    const oldIdByName = {};
    if (entity.stockKeepingUnits && entity.stockKeepingUnits.length > 0) {
        entity.stockKeepingUnits.forEach(sku => {
            oldIdByName[sku.name] = sku.id;
        });
    }
    const getSwedishName = unit => {
        const lookupUnitLowerCase = unit.toLowerCase();
        const lookupUnitUpperCase = unit.toUpperCase();
        const lookupUnitFirstLetterUpperCase = _.upperFirst(lookupUnitLowerCase);
        return (articleTranslations.stockKeepingUnits.names[lookupUnitLowerCase] ||
            articleTranslations.stockKeepingUnits.names[lookupUnitUpperCase] ||
            articleTranslations.stockKeepingUnits.names[lookupUnitFirstLetterUpperCase] ||
            unit);
    };
    const getGrossWeight = (unit, weight, altUnitFactor) => {
        if (unit === 'kg') {
            return 1;
        }
        if (unit === 'g') {
            return 0.01;
        }
        if (unit === 'hg') {
            return 0.1;
        }
        if (unit === 'mg') {
            return 0.001;
        }
        if (weight > 0) {
            if (altUnitFactor) {
                return (weight * altUnitFactor) / 1000.0;
            }
            return weight / 1000.0;
        }
        return 0;
    };
    const newUnit = qvantiNormUnit || entity.container || entity.supplierOrderUnit || 'okänd';
    const translatedUnit = getSwedishName(newUnit).toLowerCase();
    const skuId = oldIdByName[translatedUnit] || _.uuid();
    const colliSkuId = oldIdByName[colliUnit] || _.uuid();
    const grossWeightInKg = getGrossWeight(translatedUnit, entity.weight);
    const colliWeight = entity.colliSize > 0 && grossWeightInKg > 0
        ? Math.round(1000 * entity.colliSize * grossWeightInKg) / 1000.0
        : 0;
    if (entity.orderUnit === 'single') {
        stockKeepingUnits.push({
            id: skuId,
            name: translatedUnit,
            supplierName: entity.supplierOrderUnit,
            GTIN: entity.GTIN,
            grossWeight: grossWeightInKg,
            isMainUnit: true,
            metadata: {
                supplierOrderUnit: entity.supplierOrderUnit,
            },
            quantityOfMainUnitPerUnit: 1,
            minOrderQuantity: 1,
            stepOrderIncrement: 1,
        });
        if (entity.colliSize > 1) {
            stockKeepingUnits.push({
                id: colliSkuId,
                name: colliUnit,
                grossWeight: colliWeight,
                isMainUnit: false,
                metadata: {},
                quantityOfMainUnitPerUnit: entity.colliSize,
                minOrderQuantity: 1,
                stepOrderIncrement: 1,
            });
        }
    }
    else if (entity.orderUnit === 'package') {
        if (entity.colliSize > 0) {
            stockKeepingUnits.push({
                id: skuId,
                name: translatedUnit,
                supplierName: entity.supplierOrderUnit,
                GTIN: entity.GTIN,
                grossWeight: grossWeightInKg,
                isMainUnit: true,
                metadata: {
                    supplierOrderUnit: entity.supplierOrderUnit,
                },
                quantityOfMainUnitPerUnit: 1,
                minOrderQuantity: entity.colliSize,
                stepOrderIncrement: entity.colliSize,
            });
            if (translatedUnit !== 'kolli') {
                stockKeepingUnits.push({
                    id: colliSkuId,
                    name: colliUnit,
                    grossWeight: colliWeight,
                    isMainUnit: false,
                    metadata: {},
                    quantityOfMainUnitPerUnit: entity.colliSize || 1,
                    minOrderQuantity: 1,
                    stepOrderIncrement: 1,
                });
            }
        }
    }
    else if (entity.orderUnit === 'weight') {
        stockKeepingUnits.push({
            id: skuId,
            name: translatedUnit,
            supplierName: entity.supplierOrderUnit,
            GTIN: entity.GTIN,
            grossWeight: grossWeightInKg,
            isMainUnit: true,
            metadata: {
                supplierOrderUnit: entity.supplierOrderUnit,
            },
            quantityOfMainUnitPerUnit: 1,
            minOrderQuantity: entity.minWeightIncrement,
            stepOrderIncrement: entity.minWeightIncrement,
        });
        if (entity.minWeightIncrement > 0) {
            const colliWeightForWeight = entity.minWeightIncrement > 0 && grossWeightInKg > 0
                ? Math.round(1000 * entity.minWeightIncrement * grossWeightInKg) /
                    1000.0
                : 0;
            stockKeepingUnits.push({
                id: colliSkuId,
                name: colliUnit,
                grossWeight: colliWeightForWeight,
                isMainUnit: false,
                metadata: {},
                quantityOfMainUnitPerUnit: entity.minWeightIncrement,
                minOrderQuantity: 1,
                stepOrderIncrement: 1,
            });
        }
        else if (entity.metadata && entity.metadata.qvantiColliSize > 0) {
            const colliWeightForWeight = entity.metadata.qvantiColliSize > 0 && grossWeightInKg > 0
                ? Math.round(1000 * entity.metadata.qvantiColliSize * grossWeightInKg) / 1000.0
                : 0;
            stockKeepingUnits.push({
                id: colliSkuId,
                name: colliUnit,
                grossWeight: colliWeightForWeight,
                isMainUnit: false,
                metadata: {},
                quantityOfMainUnitPerUnit: entity.metadata.qvantiColliSize,
                minOrderQuantity: undefined,
                stepOrderIncrement: undefined,
            });
        }
    }
    const hasQvantiAltUnit = entity.metadata &&
        entity.metadata.qvantiAltNormUnit &&
        entity.metadata.qvantiAltUnit &&
        entity.metadata.qvantiAltUnitFactor;
    if (hasQvantiAltUnit) {
        const altNormUnit = entity.metadata.qvantiAltNormUnit;
        const altUnitFactor = entity.metadata.qvantiAltUnitFactor;
        const altUnitId = oldIdByName[altNormUnit] || _.uuid();
        const convertedIncrement = getIncrement(altNormUnit);
        stockKeepingUnits.push({
            id: altUnitId,
            name: altNormUnit,
            supplierName: entity.metadata.qvantiAltUnit,
            grossWeight: getGrossWeight(altNormUnit, entity.weight, altUnitFactor),
            isMainUnit: false,
            metadata: {
                supplierOrderUnit: entity.metadata.qvantiAltUnit,
            },
            quantityOfMainUnitPerUnit: altUnitFactor,
            minOrderQuantity: convertedIncrement,
            stepOrderIncrement: convertedIncrement,
        });
    }
    return { ...entity, stockKeepingUnits };
};
module.exports = entity => {
    let result = {
        ...entity,
    };
    result = { ...ensureSalesPeriod(result) };
    if (entity.utilityArticle) {
        result.productCategory = null;
    }
    if (!entity.shortName && entity.name) {
        result.shortName = entity.name;
    }
    const shortName = result.shortName;
    const alcoholPercentage = _.get(entity, 'alcoholPercentage') || 0; // because null is returned by _.get
    const productCategory = _.get(entity, 'productCategory');
    if (productCategory && !entity.utilityArticle) {
        const bookKeepingAccounts = calculateBookKeepingAccounts(entity, productCategory, alcoholPercentage);
        const VAT = calculateVAT(productCategory, alcoholPercentage);
        const requiresAlcoholPermit = calculateRequiresAlcoholPermit(productCategory, alcoholPercentage);
        const immutableFields = getImmutablefields(productCategory);
        const alcoholTaxClass = calculateAlcoholTaxClass(entity, productCategory, alcoholPercentage);
        const exciseProductCode = entity.exciseProductCode || calculateProductExciseCode(alcoholTaxClass);
        result = _.merge({}, result, { VAT }, bookKeepingAccounts, { requiresAlcoholPermit, alcoholTaxClass, exciseProductCode }, immutableFields);
    }
    if (!entity.onCopack && entity.orderUnit === 'single' && !entity.colliSize) {
        result.colliSize = 1;
    }
    if (entity.orderUnit === 'weight') {
        result.volume = null;
        result.weight = 1000;
        result.priceComparisonUnit = 'kg';
        result.recycleType = 'none';
        result.colliSize = 1;
    }
    if (!entity.orderUnit) {
        const colliSize = _.get(entity, 'colliSize', 1);
        if (colliSize > 1) {
            result.orderUnit = 'package';
        }
        else {
            result.orderUnit = 'single';
        }
    }
    if (!result.colliSize) {
        result.colliSize = 1;
    }
    if (!result.currency) {
        result.currency = _.get(entity, 'supplier.currency') || 'SEK';
    }
    const articleVAT = _.get(result, 'VAT', 0);
    if (!result.recycleType) {
        result.recycleType = 'none';
    }
    const recycleType = result.recycleType;
    switch (recycleType) {
        case 'none':
            result.recycleAmount = 0;
            break;
        case 'aluminiumCan':
            result.recycleAmount = _.round(1 / (1 + articleVAT / 100), 2);
            break;
        case 'PET1':
            result.recycleAmount = _.round(1 / (1 + articleVAT / 100), 2);
            break;
        case 'PET2':
            result.recycleAmount = _.round(2 / (1 + articleVAT / 100), 2);
            break;
        case 'glassBottle33cl':
            result.recycleAmount = _.round(0.6 / (1 + articleVAT / 100), 2);
            break;
        case 'glassBottle50cl':
            result.recycleAmount = _.round(0.9 / (1 + articleVAT / 100), 2);
            break;
        default:
            break;
    }
    const newExciseProductCode = findProductCode(result.knNumber);
    if (newExciseProductCode !== undefined) {
        result.exciseProductCode = newExciseProductCode;
    }
    result.alcoholTax = alcoholTaxCalculator(result);
    const newName = calculateName(_.get(entity, 'volume'), _.get(entity, 'container'), entity.brand, shortName);
    if (!entity.name && newName) {
        result.name = newName;
    }
    result.shortName = shortName;
    if (!result.onCopack) {
        result.isAvailableForPreorder = false;
    }
    if (result.articleBatches) {
        result.articleBatches = ensureArticleBatches(result.articleBatches);
        result.quantityInStock = calculateQuantityInStock(result.articleBatches);
    }
    const valuesToSetIfNulls = {
        showInNews: true,
        active: true,
        recycleType: 'none',
        recycleAmount: 0,
        promoteAsNewStart: moment.utc(),
        promoteAsNewEnd: moment().add(40, 'days').utc(),
    };
    result.articleLinks = clearNullLinks(result);
    result = ensureDropShippingArticle(result);
    result = ensureIsCopack(result);
    result = ensurePipeStringOrigin(result);
    result.colliSize = ensurePrivabColliSize(result);
    result.articleNumber = ensurePrivabArticleNumber(result);
    result = ensureSKU(result);
    result.keywords = setKeywords(entity);
    return _.merge({}, valuesToSetIfNulls, result);
};
